import { ElementType } from 'react';
import { formatUserRole, UserRole } from '@/models/User';
import {
  AdminBillingHomePath,
  AdminStoreListPath,
  OrganizationListPath,
  RetailerListPath,
  SupplierListPath,
  UserListPath,
} from '@/modules/admin/paths';
import { LogoutPath } from '@/modules/auth/paths';
import { OrderListPath } from '@/modules/order/paths';
import {
  RetailerHomePath,
  RetailerOrdersPath,
  RetailerSettingsPath,
} from '@/modules/retailer/paths';
import { ShopPath, StoreSettingsPath } from '@/modules/store/paths';
import { SupplierHomePath } from '@/modules/supplier/paths';
import {
  ArrowRightStartOnRectangleIcon,
  ArrowsRightLeftIcon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  Cog6ToothIcon,
  ShoppingBagIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';

export type HeaderIcon = ElementType;

export interface HeaderSubNavigationItem {
  name: string;
  description: string;
  href: string;
  icon?: HeaderIcon;
}

export interface HeaderNavigationItem {
  name: string;
  href?: string;
  items?: HeaderSubNavigationItem[];
}

export interface HeaderUserMenuItem {
  name: string;
  href: string;
  onClick?: () => void;
  icon?: HeaderIcon;
  role?: UserRole;
}

const settingsMenuItem: HeaderUserMenuItem = {
  name: 'Settings',
  href: StoreSettingsPath.profile,
  icon: Cog6ToothIcon,
};

const retailerSettingsMenuItem: HeaderUserMenuItem = {
  name: 'Settings',
  href: RetailerSettingsPath.profile,
  icon: Cog6ToothIcon,
};

const signOutMenuItem: HeaderUserMenuItem = {
  name: 'Sign out',
  href: LogoutPath,
  icon: ArrowRightStartOnRectangleIcon,
};

const baseUserMenuItems: HeaderUserMenuItem[] = [settingsMenuItem];

const storeNavigation: HeaderNavigationItem[] = [
  { name: 'Shop', href: ShopPath.path },
  { name: 'Orders', href: OrderListPath },
];

const supplierNavigation: HeaderNavigationItem[] = [
  { name: 'Home', href: SupplierHomePath },
  { name: 'Orders', href: OrderListPath },
];

const retailerNavigation: HeaderNavigationItem[] = [
  { name: 'Stores', href: RetailerHomePath },
  { name: 'Orders', href: RetailerOrdersPath },
];

const adminNavigation: HeaderNavigationItem[] = [
  {
    name: 'Customers',
    items: [
      {
        name: 'Organizations',
        description: 'Manage customer organizations and their billing relationships.',
        href: OrganizationListPath,
        icon: BuildingOffice2Icon,
      },
      {
        name: 'Retailers',
        description: 'Manage retailer accounts, stores, and customer-facing setup.',
        href: RetailerListPath,
        icon: ShoppingBagIcon,
      },
      {
        name: 'Stores',
        description: 'Browse and manage stores across all retailers.',
        href: AdminStoreListPath,
        icon: BuildingStorefrontIcon,
      },
      {
        name: 'Suppliers',
        description: 'Manage supplier accounts and their linked customer records.',
        href: SupplierListPath,
        icon: TruckIcon,
      },
    ],
  },
  { name: 'Billing', href: AdminBillingHomePath },
  { name: 'Users', href: UserListPath },
];

export const getNavigationForRole = (role?: UserRole): HeaderNavigationItem[] => {
  if (role === UserRole.SystemAdmin) {
    return adminNavigation;
  }

  if (role === UserRole.SupplierAdmin) {
    return supplierNavigation;
  }

  if (role === UserRole.RetailerAdmin) {
    return retailerNavigation;
  }

  if (role === UserRole.StoreManager) {
    return storeNavigation;
  }

  return [];
};

interface BuildUserMenusInput {
  role?: UserRole;
  roles?: UserRole[];
  changeRole: (role: UserRole) => Promise<void>;
  onRoleChangeStart: () => void;
}

interface UserMenus {
  userMenu: HeaderUserMenuItem[];
  accountMenu: HeaderUserMenuItem[];
}

export const buildUserMenus = ({
  role,
  roles,
  changeRole,
  onRoleChangeStart,
}: BuildUserMenusInput): UserMenus => {
  const baseItems =
    role === UserRole.RetailerAdmin ? [retailerSettingsMenuItem] : baseUserMenuItems.slice();
  const userMenu = baseItems.slice();
  const accountMenu = [...baseItems, signOutMenuItem];

  const otherRoles = roles?.filter((value) => value !== role) || [];

  for (const otherRole of otherRoles) {
    const isOtherRetailerAdmin = otherRole === UserRole.RetailerAdmin;
    const label = isOtherRetailerAdmin
      ? 'Leave store'
      : `Change to ${formatUserRole(otherRole)} role`;

    const item = {
      name: label,
      onClick: async () => {
        onRoleChangeStart();
        await changeRole(otherRole);
      },
      href: '#',
      icon: ArrowsRightLeftIcon,
    };

    userMenu.push(item);
    accountMenu.splice(accountMenu.length - 1, 0, item);
  }

  return {
    userMenu,
    accountMenu,
  };
};
